import React from 'react';
import { Mail, Clock, AlertTriangle } from 'lucide-react';

/**
 * Visual timeline component showing email sequence
 * Displays emails with their delays in a timeline format
 */
export function EmailTimeline({ emails = [], templates = [] }) {
  if (!emails || emails.length === 0) {
    return (
      <div className="text-center py-4 text-gray-400 text-sm">
        No emails in sequence
      </div>
    );
  }

  // Helper to convert hours to days for display
  const hoursToDays = (hours) => {
    if (hours === 0) return 'Now';
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Day 1';
    return `Day ${days}`;
  };

  // Get template name by ID
  const getTemplateName = (templateId, isOrphaned) => {
    if (isOrphaned || !templateId) return 'Template deleted (needs update)';
    const template = templates.find(t => t.id === templateId);
    return template?.name || 'Unknown template';
  };

  // Get template subject by ID
  const getTemplateSubject = (templateId, isOrphaned) => {
    if (isOrphaned || !templateId) return 'Template no longer exists';
    const template = templates.find(t => t.id === templateId);
    return template?.subject || 'No subject';
  };

  // Check if email is orphaned
  const isEmailOrphaned = (email) => {
    if (email.orphaned) return true;
    if (!email.templateId) return true;
    return !templates.find(t => t.id === email.templateId);
  };

  // Sort emails by order
  const sortedEmails = [...emails].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-3">
      {sortedEmails.map((email, index) => {
        const delayDays = hoursToDays(email.delay || 0);
        const orphaned = isEmailOrphaned(email);
        const templateName = getTemplateName(email.templateId, orphaned);
        const templateSubject = getTemplateSubject(email.templateId, orphaned);
        const isFirst = index === 0;
        const isLast = index === sortedEmails.length - 1;

        return (
          <div key={index} className="flex items-start gap-3">
            {/* Timeline line */}
            <div className="flex flex-col items-center pt-1">
              <div className={`w-2 h-2 rounded-full ${
                orphaned 
                  ? 'bg-red-500' 
                  : isFirst 
                    ? 'bg-[#c5a059]' 
                    : 'bg-gray-300'
              }`} />
              {!isLast && (
                <div className="w-0.5 h-12 bg-gray-200 mt-1" />
              )}
            </div>

            {/* Email content */}
            <div className="flex-1 min-w-0">
              <div className={`rounded-lg p-3 border ${
                orphaned
                  ? 'bg-red-50 border-red-200'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    {orphaned ? (
                      <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                    ) : (
                      <Mail size={14} className="text-gray-400 flex-shrink-0" />
                    )}
                    <span className={`text-sm font-medium truncate ${
                      orphaned ? 'text-red-800' : 'text-gray-800'
                    }`}>
                      Email {index + 1}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Clock size={12} className={orphaned ? 'text-red-400' : 'text-gray-400'} />
                    <span className={`text-xs font-medium ${
                      orphaned ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {delayDays}
                    </span>
                  </div>
                </div>
                <p className={`text-xs font-medium mb-1 truncate ${
                  orphaned ? 'text-red-700' : 'text-gray-600'
                }`}>
                  {templateName}
                </p>
                <p className={`text-xs line-clamp-1 ${
                  orphaned ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {templateSubject}
                </p>
                {orphaned && (
                  <p className="text-xs text-red-600 mt-1 font-medium">
                    ⚠️ Template deleted - needs update
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}



